import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import styles from './WorkExperienceDisplay.module.scss';

/**
 * @param {{ start_date?: string, end_date?: string }} item
 * @returns {string}
 */
/**
 * @param {Array<{ start_date?: string, id?: number }>} items
 * @returns {Array<{ start_date?: string, id?: number }>}
 */
export function sortWorkExperienceByStartDateDesc(items) {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return [...items].sort((a, b) => {
    const aStart =
      a.start_date && moment(a.start_date).isValid()
        ? moment(a.start_date).valueOf()
        : 0;
    const bStart =
      b.start_date && moment(b.start_date).isValid()
        ? moment(b.start_date).valueOf()
        : 0;

    if (bStart !== aStart) {
      return bStart - aStart;
    }

    return (b.id || 0) - (a.id || 0);
  });
}

export function formatWorkExperienceDateRange(item) {
  const { start_date: startDate, end_date: endDate } = item;
  const hasStart = startDate && moment(startDate).isValid();
  const hasEnd = endDate && moment(endDate).isValid();
  const endLabel = hasEnd ? moment(endDate).format('YYYY') : 'Present';

  if (hasStart) {
    const startLabel = moment(startDate).format('YYYY');
    return `${startLabel} - ${endLabel}`;
  }
  return '';
}

const workExperienceItemShape = {
  job_title: PropTypes.string,
  company_name: PropTypes.string,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
  description: PropTypes.string,
};

export const WorkExperienceCard = ({ item, className }) => {
  const dateRange = formatWorkExperienceDateRange(item);
  const title = item.job_title || '';
  const company = item.company_name || '';
  const description = item.description || '';

  return (
    <article className={`${styles.card} ${className || ''}`.trim()}>
      <div className={styles.topRow}>
        {title ? <h3 className={styles.jobTitle}>{title}</h3> : null}
        {dateRange ? (
          <span className={styles.dateRange}>{dateRange}</span>
        ) : null}
      </div>
      {company ? <div className={styles.company}>{company}</div> : null}
      {description ? <p className={styles.description}>{description}</p> : null}
    </article>
  );
};

WorkExperienceCard.propTypes = {
  item: PropTypes.shape(workExperienceItemShape).isRequired,
  className: PropTypes.string,
};

WorkExperienceCard.defaultProps = {
  className: '',
};

const WorkExperienceDisplay = ({ title, items, className }) => {
  const sortedItems = sortWorkExperienceByStartDateDesc(items);

  if (!sortedItems.length) {
    return null;
  }

  return (
    <section className={`${styles.section} ${className || ''}`.trim()}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.list}>
        {sortedItems.map((item, index) => (
          <WorkExperienceCard
            key={item.id != null ? String(item.id) : `we-${index}`}
            item={item}
          />
        ))}
      </div>
    </section>
  );
};

WorkExperienceDisplay.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape(workExperienceItemShape)),
  className: PropTypes.string,
};

WorkExperienceDisplay.defaultProps = {
  title: 'Work Experience',
  items: [],
  className: '',
};

export default WorkExperienceDisplay;
